import { useCallback, useMemo, useState } from 'react'
import { RectangularInputFormatNumberSmall, RectangularInputSmall } from '../../../../../../forms/Input'
import { RectangularInputCalendarStateFull } from '../../../../../../forms/Input/InputCalendar'
import { RectangularInputDiscount } from '../../../../../../forms/Input/InputDiscount'
import { InputDiscountValue, MaxDiscount } from '../../../../../../forms/Input/InputDiscount/types'
import InputQuantity from '../../../../../../forms/Input/InputQuantity'
import { CustomChangeFunction } from '../../../../../../forms/Input/types'
import { getCurrencySymbol } from '../../../../../../utils/globals/price'
import { RowData } from '../../../../types'
import { ITInputsOnChangeInputs } from './types'
import { Modify } from '../../../../../../utils/globals/types'
import { getUserInfo } from '../../../../../../utils/reducers/getters'
import CustomSelect, { CustomSelectProps } from '../../../../../../forms/CustomSelect'
import { DropDownWithSearchBar } from '../../../../../../forms/CustomSelect/DropDown'
import { addDictionaryExtraLine, dictionaryComplexFromJsonArr, TwinDictionary } from '../../../../../../utils/globals/dictionary'
import { emptyExtraLineFirstSortFunction } from '../../../../../../forms/CustomSelect/functions'
import TwinTrans from '../../../../../TwinTrans'
import { CustomSelectBlockedItemsProps, CustomSelectBlockedItemsSearchbar } from '../../../../../../forms/CustomSelect/CustomSelectBlockedItems'
import { ITLayoutTableModalOpenSetTy } from '../../types'
import { CustomRenderString } from '../../../../VirtualTableListing/CustomRenders'
import useTwinTranslation from '../../../../../../utils/hooks/useTwinTranslation'
import { displayDate } from '../../../../../../utils/globals/date'
import { blockedItemsFncITSpecificPriceRule, checkIfScaleProduct, checkIfScaleProductWithoutVariables, sortITSpecificPriceRule } from './function'
import useMyDebounce from '../../../../../../utils/hooks/useMyDebounce'
import { checkFloatValid } from '../../../../../../utils/globals/numbers'

export type ITInputPriceStateFullProps = Modify<ITInputPriceProps, ITInputPriceStateFullLogicProps & {
    onChange?: never
}>

export const ITInputPriceStateFull: React.FC<ITInputPriceStateFullProps> = ({ initialValue, onBlur,  ...params }) => {
    const { handleValue, value, myOnBlur } = useITInputPriceStateFullLogic({ initialValue, onBlur })
    return (
        <ITInputPrice {...params} onChange={(_1, _2, value) => handleValue(value)} value={value} onBlur={myOnBlur}/>
    )
}
interface ITInputPriceStateFullLogicProps {
    initialValue: number
    onBlur: (value: string) => void
}

const useITInputPriceStateFullLogic = ({ initialValue, onBlur }: ITInputPriceStateFullLogicProps) => {
    const [value, setValue] = useState<string>(String(initialValue))
    const handleValue = useCallback((newValue: string) => {
        setValue(newValue)
    }, [setValue])
    const myOnBlur = useCallback((e: any) => {
        const val = e?.currentTarget?.value ?? value
        const validQuantity = checkFloatValid(val) || ''
        onBlur(validQuantity)
    }, [onBlur, value])
    return { handleValue, value, myOnBlur }
}

export interface ITInputPriceProps extends ITInputPriceLogicProps {
    CurrencyId: string
    value?: string
    readOnlyFnc?: (allRowData: RowData) => boolean
    className?: string
    onBlur?: React.FocusEventHandler<HTMLInputElement>
    onFocus?: React.FocusEventHandler<HTMLInputElement>
}

export const ITInputPrice: React.FC<ITInputPriceProps> = ({ CurrencyId, value, onChange, dataKey, allRowData, readOnlyFnc, onBlur, onFocus, className }) => {
    const { handleValue } = useITInputPriceLogic({ allRowData, onChange, dataKey })
    const readOnly = readOnlyFnc?.(allRowData) || false
    const { changeMyValue, myValue, ref, myOnBlur } = useMyDebounce({ value: value as any, onChange: handleValue, readOnly, onBlur: onBlur, timeDebounce: 300 })
    const symbolCurrency = getCurrencySymbol(CurrencyId)
    return (
        <div className='flex items-center input_price'>
            <RectangularInputFormatNumberSmall value={myValue || ''} onChange={changeMyValue} {...{ readOnly, onBlur: myOnBlur, onFocus, className, myref: ref }} />
            <span className='ml-10 mr-5'>{symbolCurrency}</span>
        </div>
    )
}

interface ITInputPriceLogicProps {
    dataKey: string
    onChange: ITInputsOnChangeInputs
    allRowData: RowData
}

const useITInputPriceLogic = ({ allRowData, onChange, dataKey }: ITInputPriceLogicProps) => {
    const handleValue = useCallback((value: string) => {
        if (allRowData.id) {
            onChange?.(allRowData.absoluteIndex || allRowData.rowIndex, dataKey, value)
        }
    }, [onChange, dataKey, allRowData])

    return { handleValue }
}

export type ITInputPercentageStateFullProps = Modify<ITInputPercentageProps, ITInputPercentageStateFullLogicProps & {
    onChange?: never
}>

export const ITInputPercentageStateFull: React.FC<ITInputPercentageStateFullProps> = ({ initialValue, onBlur, ...params }) => {
    const { handleValue, value, myOnBlur } = useITInputPercentageStateFullLogic({ initialValue, onBlur })
    return (
        <ITInputPercentage {...params} onChange={(_1, _2, value) => handleValue(value)} value={value} onBlur={myOnBlur} />
    )
}
interface ITInputPercentageStateFullLogicProps {
    initialValue: number
    onBlur: (value: string) => void
}

const useITInputPercentageStateFullLogic = ({ initialValue, onBlur }: ITInputPercentageStateFullLogicProps) => {
    const [value, setValue] = useState<string>(String(initialValue))
    const handleValue = useCallback((newValue: string) => {
        setValue(newValue)
    }, [setValue])
    const myOnBlur = useCallback((e: any) => {
        const val = e?.currentTarget?.value ?? value
        const validQuantity = checkFloatValid(val) || ''
        onBlur(validQuantity)
    }, [onBlur, value])
    return { handleValue, value, myOnBlur }
}

export interface ITInputPercentageProps extends ITInputPercentageLogicProps {
    value?: string
    readOnlyFnc?: (allRowData: RowData) => boolean
    className?: string
    allowEmpty?: boolean
    onBlur?: React.FocusEventHandler<HTMLInputElement>
    onFocus?: React.FocusEventHandler<HTMLInputElement>
}

export const ITInputPercentage: React.FC<ITInputPercentageProps> = ({ value, onChange, dataKey, allRowData, readOnlyFnc, onBlur, onFocus, className, allowEmpty }) => {
    const { handleValue } = useITInputPercentageLogic({ allRowData, onChange, dataKey })
    const readOnly = readOnlyFnc?.(allRowData) || false
    const { changeMyValue, myValue, ref, myOnBlur } = useMyDebounce({ value: value as any, onChange: handleValue, readOnly, onBlur: onBlur, timeDebounce: 300 })
    return (
        <div className='flex items-center'>
            <RectangularInputFormatNumberSmall value={myValue || ''} onChange={changeMyValue} {...{ readOnly, onBlur: myOnBlur, onFocus, className, myref: ref, allowEmpty }} />
            <span className='ml-10 mr-5'>%</span>
        </div>
    )
}

interface ITInputPercentageLogicProps {
    dataKey: string
    onChange: ITInputsOnChangeInputs
    allRowData: RowData
}

const useITInputPercentageLogic = ({ allRowData, onChange, dataKey }: ITInputPercentageLogicProps) => {
    const handleValue = useCallback((value: string) => {
        if (allRowData.id) {
            onChange?.(allRowData.absoluteIndex || allRowData.rowIndex, dataKey, value)
        }
    }, [onChange, dataKey, allRowData])

    return { handleValue }
}

export interface ITInputNumericProps {
    allRowData: RowData
    value: number
    dataKey: string
    onChange: ITInputsOnChangeInputs
    readOnlyFnc?: (allRowData: RowData) => boolean
}

export const ITInputNumeric: React.FC<ITInputNumericProps> = ({ onChange, allRowData, dataKey, readOnlyFnc, ...rest }) => {
    const readOnly = readOnlyFnc?.(allRowData) || false
    return (
        <RectangularInputFormatNumberSmall className='input_table_input_numeric' onChange={(value) => onChange(allRowData.absoluteIndex || allRowData.rowIndex, dataKey, value)}  {...{ readOnly, ...rest }} />
    )
}
export interface ITRectangularInputSmallProps {
    value: string
    onChange: ITInputsOnChangeInputs
    dataKey: string
    allRowData: RowData
    className?: string
    readOnlyFnc?: (allRowData: RowData) => boolean
}

export const ITRectangularInputSmall: React.FC<ITRectangularInputSmallProps> = ({ className, value, onChange, allRowData, dataKey, readOnlyFnc }) => {
    const readOnly = readOnlyFnc?.(allRowData) || false
    return (
        <RectangularInputSmall className={className} value={value} onChange={(value) => onChange(allRowData.absoluteIndex || allRowData.rowIndex, dataKey, value)} readOnly={readOnly} />
    )
}

export interface ITInputQuantityProps extends ITInputQuantityLogicProps {
    value: number
    readOnlyFnc?: (allRowData: RowData) => boolean
    applyMaxValue?: boolean
    skipMinQuantity?: boolean
}

export const ITInputQuantity: React.FC<ITInputQuantityProps> = ({ onChange, allRowData, dataKey, onBlur, readOnlyFnc, applyMaxValue, skipMinQuantity, ...rest }) => {
    const readOnly = readOnlyFnc?.(allRowData) || checkIfScaleProduct(allRowData) || false
    const myMinValue = (!skipMinQuantity && allRowData?.minQuantity ? parseFloat(allRowData.minQuantity) : undefined)
    const myMaxValue = (applyMaxValue && allRowData?.desiredQuantity ? parseFloat(allRowData.desiredQuantity) : undefined)
    const { myOnChange, myOnBlurInner } = useITInputQuantityLogic({ allRowData, onChange, dataKey, myMaxValue, myMinValue, allowNegatives: rest.allowNegatives})
    const { changeMyValue, myValue, ref, myOnBlur } = useMyDebounce({ value: rest.value as any, onChange: myOnChange, readOnly, onBlur: myOnBlurInner, timeDebounce: 300 })
    const typeQuantity = allRowData.typeQuantity
    return (
        <InputQuantity onChange={changeMyValue}  {...{ typeQuantity, readOnly,  ...rest }} value={myValue as any} minValue={myMinValue} maxValue={myMaxValue} onBlur={myOnBlur} myref={ref} />
    )
}

interface ITInputQuantityLogicProps {
    allRowData: RowData
    onChange: ITInputsOnChangeInputs
    dataKey: string
    onBlur?: (allRowData: RowData, onChange: CustomChangeFunction) => void
    myMinValue?: number
    myMaxValue?: number
    allowNegatives?: boolean
}

const useITInputQuantityLogic = ({ onChange, allRowData, dataKey, onBlur, myMaxValue, myMinValue, allowNegatives }: ITInputQuantityLogicProps) => {
    const myOnChange: CustomChangeFunction = useCallback((value) => {
        const validQuantity = checkFloatValid(String(value), allowNegatives)
        if (validQuantity && validQuantity !== '-') {
            const validQuantityFloat = parseFloat(validQuantity)
            const minQuantityCheck = myMinValue && myMinValue > validQuantityFloat
            const maxQuantityCheck = myMaxValue && myMaxValue < validQuantityFloat
            if (!minQuantityCheck && !maxQuantityCheck) {
                onChange(allRowData.absoluteIndex || allRowData.rowIndex, dataKey, String(validQuantity))
            }
        }
    }, [onChange, dataKey, allRowData.absoluteIndex, allRowData.rowIndex, myMaxValue, myMinValue, allowNegatives])

    const myOnBlurInner = useCallback((e: any) => {
        let myValue = e.currentTarget.value
        const validQuantity = checkFloatValid(String(myValue), allowNegatives)
        const validQuantityFloat = validQuantity ? parseFloat(validQuantity) : 0
        let oldValue = allRowData[dataKey]
        if (validQuantity === '' || validQuantity === '-') {
            onChange(allRowData.absoluteIndex || allRowData.rowIndex, dataKey, myMinValue || 0 as any)
        } else if (myMinValue && myMinValue > validQuantityFloat) {
            onChange(allRowData.absoluteIndex || allRowData.rowIndex, dataKey, myMinValue as any)
        } else if (myMaxValue && myMaxValue < validQuantityFloat) {
            onChange(allRowData.absoluteIndex || allRowData.rowIndex, dataKey, myMaxValue as any)
        } else if (validQuantity && validQuantity !== oldValue) {
            onChange(allRowData.absoluteIndex || allRowData.rowIndex, dataKey, validQuantity)
        }
        onBlur?.(allRowData, myOnChange)
    }, [onChange, dataKey, allRowData, myOnChange, onBlur, myMinValue, myMaxValue, allowNegatives])

    return { myOnChange, myOnBlurInner }
}

export const ITInputQuantity2: React.FC<ITInputQuantityProps> = ({ onChange, allRowData, dataKey, onBlur, readOnlyFnc, ...rest }) => {
    const readOnly = readOnlyFnc?.(allRowData) || false
    const typeQuantity2 = allRowData.typeQuantity2
    const myOnChange: CustomChangeFunction = (value) => onChange(allRowData.absoluteIndex || allRowData.rowIndex, dataKey, value)
    return (
        <InputQuantity onChange={myOnChange} typeQuantity={typeQuantity2} {...{ readOnly, ...rest }} onBlur={() => onBlur?.(allRowData, myOnChange)} />
    )
}

export const ITInputQuantityRefund: React.FC<ITInputQuantityProps> = ({ onChange, allRowData, dataKey, onBlur, readOnlyFnc, applyMaxValue, skipMinQuantity, ...rest }) => {
    const readOnly = readOnlyFnc?.(allRowData) || parseFloat(allRowData?.restRefundQuantity) === 0 || checkIfScaleProductWithoutVariables(allRowData) || false
    const myMinValue = (!skipMinQuantity && allRowData?.minQuantity ? parseFloat(allRowData.minQuantity) : undefined)
    const myMaxValue = (applyMaxValue && allRowData?.restRefundQuantity ? parseFloat(allRowData.restRefundQuantity) : undefined)
    const { myOnChange, myOnBlurInner } = useITInputQuantityLogic({ allRowData, onChange, dataKey, myMaxValue, myMinValue })
    const { changeMyValue, myValue, ref, myOnBlur } = useMyDebounce({ value: rest.value as any, onChange: myOnChange, readOnly, onBlur: myOnBlurInner, timeDebounce: 300 })
    const typeQuantity = allRowData.typeQuantity
    return (
        <InputQuantity onChange={changeMyValue}  {...{ typeQuantity, readOnly, ...rest }} value={String(myValue) as any} minValue={myMinValue} maxValue={myMaxValue || 0} onBlur={myOnBlur} myref={ref} />
    )
}

export interface ITInputCalendarProps {
    allRowData: RowData
    value: string
    dataKey: string
    onChange: ITInputsOnChangeInputs
    readOnlyFnc?: (allRowData: RowData) => boolean
}

export const ITInputCalendar: React.FC<ITInputCalendarProps> = ({ value, onChange, allRowData, dataKey, readOnlyFnc }) => {
    const readOnly = readOnlyFnc?.(allRowData) || false
    if (readOnly) {
        return <span>{allRowData[dataKey] ? displayDate(new Date(allRowData[dataKey])): ''}</span>
    }
    return (
        <RectangularInputCalendarStateFull value={value} onChange={(value) => onChange(allRowData.absoluteIndex || allRowData.rowIndex, dataKey, value)} label={''} onlyValids={true} readOnly={readOnly} />
    )
}

export interface ITInputDiscountProps extends ITInputDiscountLogicProps {
    value: number
    CurrencyId: string
    readOnlyFnc?: (allRowData: RowData) => boolean
}

export const ITInputDiscount: React.FC<ITInputDiscountProps> = ({ allRowData, onChange, CurrencyId, readOnlyFnc, ...rest }) => {
    const { customOnChange, maxDiscount } = useITInputDiscountLogic({ allRowData, onChange, ...rest })
    const readOnly = readOnlyFnc?.(allRowData) || false
    return (
        <RectangularInputDiscount value={{ discount: allRowData.discount, typeDiscount: allRowData.typeDiscount }} onChange={customOnChange} className={'input_table_input_discount'} {...{ CurrencyId, readOnly, maxDiscount }} />
    )
}

interface ITInputDiscountLogicProps {
    onChange: ITInputsOnChangeInputs
    allRowData: RowData
    priceField?: string
}

const useITInputDiscountLogic = ({ allRowData, onChange, priceField = 'initialUnitPriceWithoutTax' }: ITInputDiscountLogicProps) => {
    const maxDiscount: MaxDiscount = useMemo(() => {
        const user = getUserInfo()
        return {
            maxPercentageDiscount: user?.Employee?.maxDiscount ? parseFloat(String(user?.Employee?.maxDiscount)) : 100,
            maxTotalDiscount: parseFloat(allRowData.quantity) * parseFloat(allRowData[priceField]),
            maxUnitDiscount: parseFloat(allRowData[priceField])
        }
    }, [allRowData, priceField])

    const customOnChange = useCallback((value: InputDiscountValue) => {
        const changedDiscount = String(value.discount) !== String(allRowData.discount)
        const changedType = String(value.typeDiscount) !== String(allRowData.typeDiscount)
        if (changedType) {
            onChange(allRowData.absoluteIndex || allRowData.rowIndex, 'typeDiscount', String(value.typeDiscount) || '0')
        } else if (changedDiscount) {
            onChange(allRowData.absoluteIndex || allRowData.rowIndex, 'discount', value.discount || '')
        }
    }, [allRowData, onChange])

    return { customOnChange, maxDiscount }
}


export type ITSelectSpecificPriceRuleProps = CustomSelectBlockedItemsProps & ITSelectSpecificPriceRuleLogicProps & {
    allRowData: RowData
    readOnlyFnc?: (allRowData: RowData) => boolean
    CurrencyId: string
}

export const ITSelectSpecificPriceRule: React.FC<ITSelectSpecificPriceRuleProps> = ({ readOnlyFnc, allRowData, onChange, dataKey, CurrencyId, ...props }) => {
    const readOnly = readOnlyFnc?.(allRowData) || false
    const { myOnChange } = useITSelectSpecificPriceRuleLogic({ onChange, dataKey, allRowData })
    if (readOnly && !props?.value) {
        return null
    }
    return (<CustomSelectBlockedItemsSearchbar {...props} label='' onChange={myOnChange} readOnly={readOnly} fieldName='fullname' sortFunction={sortITSpecificPriceRule} blockItemsFnc={(items) => blockedItemsFncITSpecificPriceRule(items, CurrencyId)}/>)
}

interface ITSelectSpecificPriceRuleLogicProps {
    allRowData: RowData
    onChange: ITInputsOnChangeInputs
    dataKey: string
}

const useITSelectSpecificPriceRuleLogic = ({ allRowData, dataKey, onChange }: ITSelectSpecificPriceRuleLogicProps) => {
    const myOnChange: CustomChangeFunction = useCallback((value) => {
        onChange(allRowData.absoluteIndex || allRowData.rowIndex, dataKey, value)
    }, [onChange, dataKey, allRowData.absoluteIndex, allRowData.rowIndex])
    return { myOnChange }
}

export type ITInputSelectWithSeachBarProps = CustomSelectProps & ITInputSelectLogicProps & { }

export const ITInputSelectWithSeachBar: React.FC<ITInputSelectWithSeachBarProps> = ({ items, onChange, dataKey, allRowData, ...all }) => {
    const { myOnChange } = useITInputSelectLogic({ onChange, dataKey, allRowData })
    if (!allRowData?.ProductId) {
        return null
    }
    return (<CustomSelect items={items} {...all} label={all.value ? '' : all.label} DropDownComponent={DropDownWithSearchBar} onChange={myOnChange} className='w-full' hideLabel={true}/>)
}

interface ITInputSelectLogicProps {
    allRowData: RowData
    onChange: ITInputsOnChangeInputs
    dataKey: string
}

const useITInputSelectLogic = ({ allRowData, onChange, dataKey }: ITInputSelectLogicProps) => {
    const myOnChange: CustomChangeFunction = useCallback((value) => {
        onChange(allRowData.absoluteIndex || allRowData.rowIndex, dataKey, value)
    }, [onChange, dataKey, allRowData.absoluteIndex, allRowData.rowIndex])

    return { myOnChange }
}
export interface ITInputSelectAccountsProps extends ITInputSelectAccountsLogicProps {
    accounts: TwinDictionary[]
    label: string
    value: string
}

export const ITInputSelectAccounts: React.FC<ITInputSelectAccountsProps> = ({ accounts, label, value, ...all }) => {
    const { myOnChange } = useITInputSelectAccountsLogic({ ...all })
    const myAccounts = []
    if (accounts && accounts.length) {
        for (const element of accounts) {
            if (element.name !== '--other') {
                myAccounts.push(element)
            }
        }
    } else {
        return <TwinTrans transKey='noChapters'>No hay capítulos</TwinTrans>
    }
    let myItems = dictionaryComplexFromJsonArr(myAccounts || [])
    const myValue = myItems?.[value] ? value : ''
    return (<CustomSelect items={addDictionaryExtraLine(myItems)} onChange={myOnChange} DropDownComponent={DropDownWithSearchBar} label={myValue ? '' : label} className='w-full' value={myValue} sortFunction={emptyExtraLineFirstSortFunction} />)
}

interface ITInputSelectAccountsLogicProps {
    allRowData: RowData
    onChange: ITInputsOnChangeInputs
    dataKey: string
}

const useITInputSelectAccountsLogic = ({ allRowData, onChange, dataKey }: ITInputSelectAccountsLogicProps) => {
    const myOnChange: CustomChangeFunction = useCallback((value) => {
        onChange(allRowData.absoluteIndex || allRowData.rowIndex, dataKey, value)
    }, [onChange, dataKey, allRowData.absoluteIndex, allRowData.rowIndex])

    return { myOnChange }
}
export interface ITInputSelectWarehousesProps extends ITInputSelectWarehousesLogicProps {
    items: TwinDictionary
    label: string
    value: string
    readOnlyFnc?: (allRowData: RowData) => boolean
    setOpenModal: ITLayoutTableModalOpenSetTy
    addAnyWarehouses?: boolean
}

export const ITInputSelectWarehouses: React.FC<ITInputSelectWarehousesProps> = ({ items, label, value, allRowData, readOnlyFnc, addAnyWarehouses = true, ...rest }) => {
    const { t } = useTwinTranslation()
    const { myOnChange } = useITInputSelectWarehousesLogic({ allRowData, ...rest })
    const readOnly = readOnlyFnc?.(allRowData) || false
    if (!allRowData?.ProductId) {
        return null
    }
    if (!items) {
        return <TwinTrans transKey='noWarehouses'>No hay almacenes</TwinTrans>
    }
    let myItems = items
    if (addAnyWarehouses) {
        const anyWarehouse = { 0: { id: 0, name: t('anyWarehouse', 'Cualquier almacén')}}
        myItems = { ...anyWarehouse, ...items }
    }
    const myValue = value || 0
    if (readOnly) {
        return <ITOrderWarehouse setOpenModal={rest.setOpenModal} allRowData={allRowData} warehouses={myItems}/>
    }
    return (<CustomSelect items={myItems} onChange={myOnChange} DropDownComponent={DropDownWithSearchBar} label={''} className='w-full' value={myValue} readOnly={readOnly} />)
}

interface ITInputSelectWarehousesLogicProps {
    allRowData: RowData
    onChange: ITInputsOnChangeInputs
    dataKey: string
}

const useITInputSelectWarehousesLogic = ({ allRowData, onChange, dataKey }: ITInputSelectWarehousesLogicProps) => {
    const myOnChange: CustomChangeFunction = useCallback((value) => {
        onChange(allRowData.absoluteIndex || allRowData.rowIndex, dataKey, value)
    }, [onChange, dataKey, allRowData.absoluteIndex, allRowData.rowIndex])

    return { myOnChange }
}

export interface ITOrderWarehouseProps extends ITOrderWarehouseLogicProps { }

export const ITOrderWarehouse: React.FC<ITOrderWarehouseProps> = ({ allRowData, setOpenModal, warehouses }) => {
    const { myOpenModal } = useITOrderWarehouseLogic({ allRowData, setOpenModal, warehouses })
    if (!allRowData.WarehouseId && !allRowData.warehouseExtraData) {
        return null
    }
    if (allRowData.WarehouseId && !allRowData.warehouseExtraData) {
        return <CustomRenderString value={warehouses?.[parseInt(allRowData.WarehouseId)]?.name || ''} />
    }
    return (
        <div onClick={myOpenModal} className='hover:text-green-43 cursor-pointer flex items-center text-green-43'>
            <span><TwinTrans transKey='show'>Mostrar</TwinTrans></span>
        </div>
    )
}

interface ITOrderWarehouseLogicProps {
    setOpenModal: ITLayoutTableModalOpenSetTy
    allRowData: RowData
    warehouses: TwinDictionary
}

const useITOrderWarehouseLogic = ({ allRowData, setOpenModal, warehouses }: ITOrderWarehouseLogicProps) => {
    const myOpenModal = useCallback(() => {
        allRowData.warehouses = warehouses
        setOpenModal({ allRowData, type: 'warehouses' })
    }, [setOpenModal, allRowData, warehouses])
    return { myOpenModal }
}

export interface ITInputSelectWarehousesProps extends ITInputSelectWarehousesLogicProps {
    items: TwinDictionary
    label: string
    value: string
    readOnlyFnc?: (allRowData: RowData) => boolean
    setOpenModal: ITLayoutTableModalOpenSetTy
}

export const ITInputSelectWarehousesProductType: React.FC<ITInputSelectWarehousesProps> = ({ items, label, value, allRowData, readOnlyFnc, ...rest }) => {
    const { t } = useTwinTranslation()
    const { myOnChange } = useITInputSelectWarehousesLogic({ allRowData, ...rest })
    const readOnly = readOnlyFnc?.(allRowData) || false
    if (!allRowData?.ProductTypeId) {
        return null
    }
    const anyWarehouse = { 0: { id: 0, name: t('anyWarehouse', 'Cualquier almacén') } }
    if (!items) {
        return <TwinTrans transKey='noWarehouses'>No hay almacenes</TwinTrans>
    }
    const myItems = { ...anyWarehouse, ...items }
    const myValue = value || 0
    if (readOnly) {
        return <ITOrderWarehouse setOpenModal={rest.setOpenModal} allRowData={allRowData} warehouses={myItems} />
    }
    return (<CustomSelect items={myItems} onChange={myOnChange} DropDownComponent={DropDownWithSearchBar} label={''} className='w-full' value={myValue} readOnly={readOnly} />)
}


export interface ITInputSelectProps extends ITInputSelectLogicProps {
    items: TwinDictionary
    label: string
    value: string
    readOnly?: boolean
    className?: string
}

export const ITInputSelect: React.FC<ITInputSelectProps> = ({ items, label, value, readOnly, className, ...all }) => {
    const { myOnChange } = useITInputSelectLogic({ ...all })
    const myValue = items?.[value] ? value : ''
    return (<CustomSelect items={items} onChange={myOnChange} readOnly={!all.allRowData?.OrderId} DropDownComponent={DropDownWithSearchBar} label={myValue ? '' : label} className={'w-full ' + (className || '')} value={myValue} sortFunction={emptyExtraLineFirstSortFunction} />)
}

export interface ITCheckboxProps {
    value: string
    label?: string
    onChange: (value: boolean)=>void
    dataKey: string
    allRowData: RowData
    extraProps?: TwinDictionary
    className?: string
    readOnlyFnc?: (allRowData: RowData) => boolean
    notShowFnc?: (allRowData: RowData) => boolean
}